// frontend/src/components/asset/InspectVehicleModal.js
/**
 * Driver monthly self-inspection checklist modal.
 *
 * Used from My Assets (asset card CTA) and the above-the-fold
 * MyInspectionsDue queue. Template-driven: pass/fail, yes/no, three-state,
 * odometer, and free-text items with optional fault photos on fail values.
 *
 * The last verified odometer is shown as context (not prefilled — drivers
 * still read the dash). Plausibility warnings catch likely scale/keying
 * mistakes, but never block the safety inspection: unusual readings are
 * submitted for custodian review and excluded from the trusted baseline.
 *
 * Theme: Employee (navy).
 */

import React, { useEffect, useMemo, useState } from 'react';
import {
  AlertCircle, AlertTriangle, ClipboardCheck, Gauge, X,
} from 'lucide-react';
import Modal from '../UI/Modal';
import ConfirmModal from '../UI/ConfirmModal';
import PillToggle from '../UI/PillToggle';
import { ReturnPhotoUploader } from './PendingReturnsShared';
import { useQueryClient } from '@tanstack/react-query';
import {
  useInspectionTemplate,
  useMyInspectionsDue,
} from '../../hooks/useAssetInventoryData';
import { formatDate } from '../../utils/dateUtils';
import toast from '../../utils/toast';

/** Matches VehicleInspectionPhoto model help text (5MB JPEG/PNG/WebP). */
const INSPECTION_MAX_PHOTOS_PER_ITEM = 3;
const INSPECTION_PHOTO_MAX_BYTES = 5 * 1024 * 1024;
const INSPECTION_PHOTOS_TOTAL_BYTES = 15 * 1024 * 1024;
const FALLBACK_ODOMETER_ABSOLUTE_REVIEW_KM = 1000000;

const formatKm = (km) => {
  if (km == null || km === '') return null;
  const n = Number(km);
  if (!Number.isFinite(n)) return null;
  return n.toLocaleString('en-SG');
};

/**
 * Resolve the latest known odometer from the inspections-due row or nested
 * fleet payload (asset card merge).
 *
 * @param {object|null|undefined} vehicle
 * @returns {{ km: number|null, date: string|null }}
 */
const resolveOdometerBaseline = (vehicle) => {
  const rawKm = vehicle?.last_odometer_km
    ?? vehicle?.current_mileage
    ?? vehicle?.fleet_vehicle?.current_mileage
    ?? vehicle?.fleet_vehicle_summary?.current_mileage
    ?? null;
  const date = vehicle?.last_odometer_date
    ?? vehicle?.current_mileage_date
    ?? vehicle?.fleet_vehicle?.current_mileage_date
    ?? vehicle?.fleet_vehicle_summary?.current_mileage_date
    ?? null;
  if (rawKm == null || rawKm === '') return { km: null, date: null };
  const n = Number(rawKm);
  if (!Number.isFinite(n) || n < 0) return { km: null, date: null };
  return { km: Math.trunc(n), date: date || null };
};

const parseOdometerInput = (raw) => {
  const text = String(raw ?? '').trim();
  if (!text) return { empty: true, value: null, invalid: false };
  const n = Number(text);
  if (!Number.isFinite(n) || !Number.isInteger(n) || n < 0) {
    return { empty: false, value: null, invalid: true };
  }
  return { empty: false, value: n, invalid: false };
};

const assessOdometerInput = (
  raw,
  baselineKm,
  plausibleMaxKm,
  absoluteReviewKm,
) => {
  const parsed = parseOdometerInput(raw);
  if (parsed.empty || parsed.invalid) return null;

  const aboveAbsoluteLimit = parsed.value >= absoluteReviewKm;
  const belowBaseline = baselineKm != null && parsed.value < baselineKm;
  const abovePlausibleMax = (
    plausibleMaxKm != null
    && parsed.value > plausibleMaxKm
  );
  if (!aboveAbsoluteLimit && !belowBaseline && !abovePlausibleMax) return null;

  const suggestedKm = [10, 100]
    .map((divisor) => parsed.value / divisor)
    .find((candidate) => (
      Number.isInteger(candidate)
      && (baselineKm == null || candidate >= baselineKm)
      && (plausibleMaxKm == null || candidate <= plausibleMaxKm)
    )) ?? null;

  let detail = 'This reading differs sharply from verified vehicle history.';
  if (belowBaseline) {
    detail = `This is below the last verified ${formatKm(baselineKm)} km.`;
  } else if (abovePlausibleMax) {
    detail = `This is above the expected range of up to ${formatKm(plausibleMaxKm)} km.`;
  } else if (aboveAbsoluteLimit) {
    detail = 'This exceeds the automatic odometer review threshold.';
  }

  return {
    value: parsed.value,
    detail,
    suggestedKm,
  };
};

const InspectionChecklistItem = ({
  item,
  value,
  onChange,
  remark,
  onRemarkChange,
  photos,
  onPhotosChange,
  lastOdometerKm,
  lastOdometerDate,
  odometerAssessment,
}) => {
  const isFlagged = value != null && value !== '' && (item.fail_values || []).includes(value);
  const isOdometer = item.item_type === 'odometer';
  const odoParsed = isOdometer ? parseOdometerInput(value) : null;
  const odoNeedsReview = Boolean(isOdometer && odometerAssessment);
  const odoInvalid = Boolean(isOdometer && odoParsed && odoParsed.invalid);

  const toggleOptions = useMemo(() => {
    if (item.item_type === 'pass_fail') {
      return [
        { id: 'ok', label: 'OK' },
        { id: 'needs_attention', label: 'Needs Attention' },
      ];
    }
    if (item.item_type === 'yes_no') {
      return [
        { id: 'no', label: 'No' },
        { id: 'yes', label: 'Yes' },
      ];
    }
    if (item.item_type === 'three_state') {
      return (item.options || []).map((o) => ({ id: o.value, label: o.label }));
    }
    return [];
  }, [item]);

  return (
    <div
      className={`rounded-lg border p-3 ${
        isFlagged || odoInvalid
          ? 'border-red-200 bg-red-50'
          : odoNeedsReview
            ? 'border-amber-200 bg-amber-50'
          : 'border-gray-200'
      }`}
    >
      <p className="text-sm font-medium text-gray-900">
        {item.label}{item.is_required && <span className="text-red-500"> *</span>}
      </p>
      {item.help_text && <p className="text-xs text-gray-500 mt-0.5">{item.help_text}</p>}

      <div className="mt-2">
        {(item.item_type === 'pass_fail' || item.item_type === 'yes_no' || item.item_type === 'three_state') && (
          <PillToggle
            value={value}
            onChange={onChange}
            options={toggleOptions}
            variant="text"
            theme="employee"
            fullWidth
            ariaLabel={item.label}
          />
        )}
        {isOdometer && (
          <div className="space-y-2">
            {lastOdometerKm != null && (
              <div className="rounded-lg border border-blue-100 bg-blue-50 px-2.5 py-1.5 flex items-start gap-1.5">
                <Gauge className="w-3.5 h-3.5 text-blue-700 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-blue-800">
                  Last recorded:{' '}
                  <span className="font-semibold">{formatKm(lastOdometerKm)} km</span>
                  {lastOdometerDate ? ` · ${formatDate(lastOdometerDate)}` : ''}
                  . Unusual readings are sent to the fleet custodian for review.
                </p>
              </div>
            )}
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Gauge className="h-4 w-4 text-gray-400" aria-hidden="true" />
              </div>
              <input
                type="number"
                min={0}
                step="1"
                inputMode="numeric"
                value={value ?? ''}
                onChange={(e) => onChange(e.target.value)}
                placeholder={
                  lastOdometerKm != null
                    ? `At least ${formatKm(lastOdometerKm)} km`
                    : 'Current odometer reading (km)'
                }
                aria-invalid={odoInvalid}
                aria-describedby={
                  odoNeedsReview || odoInvalid ? `odo-feedback-${item.key}` : undefined
                }
                className={`input w-full pl-9 ${odoInvalid ? 'input-error' : ''}`}
              />
            </div>
            {odoInvalid && (
              <p id={`odo-feedback-${item.key}`} className="text-xs text-red-700 flex items-center gap-1">
                <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" />
                Enter a whole number of kilometres (no decimals).
              </p>
            )}
            {odoNeedsReview && !odoInvalid && (
              <p id={`odo-feedback-${item.key}`} className="text-xs text-amber-800 flex items-start gap-1">
                <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5 text-amber-600" />
                <span>
                  {odometerAssessment.detail} You can still complete the
                  inspection; this reading will not update verified mileage
                  until a custodian reviews it.
                  {odometerAssessment.suggestedKm != null
                    ? ` Check whether you meant ${formatKm(odometerAssessment.suggestedKm)} km.`
                    : ''}
                </span>
              </p>
            )}
          </div>
        )}
        {item.item_type === 'text' && (
          <textarea
            id={`inspect-text-${item.key}`}
            value={value ?? ''}
            onChange={(e) => onChange(e.target.value)}
            placeholder="Type here..."
            className="textarea w-full"
            rows={4}
          />
        )}
      </div>

      {isFlagged && (
        <div className="mt-3 pt-3 border-t border-red-200 space-y-3">
          <p className="text-xs text-red-700 flex items-center gap-1">
            <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" />
            Flagged for the custodian&apos;s attention
          </p>
          <div>
            <label
              htmlFor={`inspect-remark-${item.key}`}
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Remark <span className="font-normal text-gray-500">(recommended)</span>
            </label>
            <input
              id={`inspect-remark-${item.key}`}
              type="text"
              value={remark || ''}
              onChange={(e) => onRemarkChange(e.target.value)}
              placeholder="What did you notice?"
              className="input w-full"
            />
          </div>
          <ReturnPhotoUploader
            photos={photos || []}
            onChange={onPhotosChange}
            label="Photos (optional)"
            helpText="Helps the custodian see the issue. Up to 3 images, 5MB each."
            maxPhotos={INSPECTION_MAX_PHOTOS_PER_ITEM}
            maxBytes={INSPECTION_PHOTO_MAX_BYTES}
            totalBytes={INSPECTION_PHOTOS_TOTAL_BYTES}
            theme="employee"
          />
        </div>
      )}
    </div>
  );
};

const InspectVehicleModal = ({ isOpen, onClose, vehicle, onSubmit, isLoading }) => {
  const queryClient = useQueryClient();
  const { data: template, isLoading: loadingTemplate } = useInspectionTemplate({ enabled: isOpen });
  // Fresh due-row mileage — the open-time vehicle snapshot can be stale
  // (React Query cache from before workshop / dummy mileage landed).
  const { data: dueRows = [] } = useMyInspectionsDue();
  const [answers, setAnswers] = useState({});
  const [remarks, setRemarks] = useState({});
  const [photos, setPhotos] = useState({});
  const [showOdometerConfirm, setShowOdometerConfirm] = useState(false);
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);

  useEffect(() => {
    if (!isOpen) return undefined;
    queryClient.invalidateQueries({ queryKey: ['vehicle-inspections', 'my-due'] });
    return undefined;
  }, [isOpen, queryClient]);

  const dueRow = useMemo(() => {
    const assetId = vehicle?.asset_id;
    if (assetId == null) return null;
    return (dueRows || []).find((r) => Number(r.asset_id) === Number(assetId)) || null;
  }, [dueRows, vehicle?.asset_id]);

  const { km: lastOdometerKm, date: lastOdometerDate } = useMemo(
    () => resolveOdometerBaseline({
      ...dueRow,
      ...vehicle,
      last_odometer_km: vehicle?.last_odometer_km ?? dueRow?.last_odometer_km ?? null,
      last_odometer_date: vehicle?.last_odometer_date ?? dueRow?.last_odometer_date ?? null,
    }),
    [vehicle, dueRow],
  );
  const odometerPlausibleMaxKm = (
    vehicle?.odometer_plausible_max_km
    ?? dueRow?.odometer_plausible_max_km
    ?? null
  );
  const odometerAbsoluteReviewKm = (
    vehicle?.odometer_absolute_review_km
    ?? dueRow?.odometer_absolute_review_km
    ?? FALLBACK_ODOMETER_ABSOLUTE_REVIEW_KM
  );

  // Reset when opening for a vehicle (asset id — not object identity).
  // Odometer is intentionally left blank — last km lives in the banner so
  // the driver still has to read the dash.
  useEffect(() => {
    if (!isOpen) return;
    setAnswers({});
    setRemarks({});
    setPhotos({});
    setShowOdometerConfirm(false);
    setShowDiscardConfirm(false);
  }, [isOpen, vehicle?.asset_id]);

  const items = template?.items || [];
  const odometerItem = items.find((item) => item.item_type === 'odometer');
  const odometerAssessment = useMemo(
    () => assessOdometerInput(
      odometerItem ? answers[odometerItem.key] : '',
      lastOdometerKm,
      odometerPlausibleMaxKm,
      odometerAbsoluteReviewKm,
    ),
    [
      answers,
      odometerItem,
      lastOdometerKm,
      odometerPlausibleMaxKm,
      odometerAbsoluteReviewKm,
    ],
  );

  const odometerOk = useMemo(() => {
    if (!odometerItem) return true;
    const parsed = parseOdometerInput(answers[odometerItem.key]);
    if (odometerItem.is_required && parsed.empty) return false;
    if (parsed.empty) return true;
    return !parsed.invalid;
  }, [odometerItem, answers]);

  const isComplete = items.every((item) => {
    if (!item.is_required) return true;
    const val = answers[item.key];
    return val !== undefined && val !== null && String(val).trim() !== '';
  }) && odometerOk;

  const handleAnswerChange = (key, value) => {
    setAnswers((prev) => ({ ...prev, [key]: value }));
  };

  const submitInspection = () => {
    // One free-text surface (template text items, e.g. Notes / Other). Mirror
    // into inspection.notes so the custodian detail view has a single place
    // for driver commentary — not a second parallel field on the form.
    const notes = items
      .filter((item) => item.item_type === 'text')
      .map((item) => String(answers[item.key] || '').trim())
      .filter(Boolean)
      .join('\n\n');
    // No date field — the server stamps today's local date and tracks
    // completion against the current calendar month.
    onSubmit({ answers, remarks, photos, notes });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!isComplete || isLoading) return;
    if (!odometerOk) {
      toast.error('Enter the odometer as a whole number of kilometres.');
      return;
    }
    if (odometerAssessment) {
      setShowOdometerConfirm(true);
      return;
    }
    submitInspection();
  };

  const isDirty = (
    Object.values(answers).some((value) => String(value ?? '').trim() !== '')
    || Object.values(remarks).some((value) => String(value ?? '').trim() !== '')
    || Object.values(photos).some((files) => Array.isArray(files) && files.length > 0)
  );

  const requestClose = () => {
    if (isLoading || showOdometerConfirm || showDiscardConfirm) return;
    if (isDirty) {
      setShowDiscardConfirm(true);
      return;
    }
    onClose();
  };

  const discardAndClose = () => {
    setShowDiscardConfirm(false);
    setAnswers({});
    setRemarks({});
    setPhotos({});
    onClose();
  };

  const vehicleLabel = [
    vehicle?.brand,
    vehicle?.model,
  ].filter(Boolean).join(' ') || vehicle?.vehicle_type_display || '';

  return (
    <>
      <Modal
      isOpen={isOpen}
      onClose={requestClose}
      size="md"
      theme="employee"
      // Plate stays in sticky header chrome — do NOT sticky the full info
      // card (third sticky band eats mobile checklist space).
      customHeader={(
        <div className="modal-header">
          <div className="min-w-0 flex-1">
            <h3 className="modal-title">Monthly Vehicle Inspection</h3>
            <p className="text-sm text-gray-600 mt-0.5 truncate">
              <span className="font-semibold text-gray-900">
                {vehicle?.vehicle_number || 'Vehicle'}
              </span>
              {vehicleLabel ? ` · ${vehicleLabel}` : ''}
            </p>
          </div>
          <button
            type="button"
            onClick={requestClose}
            className="modal-close flex-shrink-0"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      )}
      footer={
        <>
          <button type="button" onClick={requestClose} className="btn btn-outline">
            Cancel
          </button>
          <button
            type="submit"
            form="inspect-vehicle-form"
            disabled={!isComplete || isLoading || loadingTemplate}
            className="btn btn-primary"
          >
            <ClipboardCheck className="w-4 h-4 mr-2" />
            {isLoading ? 'Submitting...' : 'Submit Inspection'}
          </button>
        </>
      }
    >
      <form id="inspect-vehicle-form" onSubmit={handleSubmit} className="space-y-4">
        <div className="bg-blue-50 rounded-lg p-3">
          <p className="text-xs text-blue-800">
            {vehicle?.last_inspection_date
              ? `Last submitted: ${formatDate(vehicle.last_inspection_date)}`
              : 'No prior inspection on file'}
            {' · '}Submitting records today&apos;s date automatically.
          </p>
        </div>

        {loadingTemplate ? (
          <p className="text-sm text-gray-500 text-center py-6">Loading checklist...</p>
        ) : items.length === 0 ? (
          <p className="text-sm text-gray-500 text-center py-6">No active checklist configured.</p>
        ) : (
          <div className="space-y-3">
            {items.map((item) => (
              <InspectionChecklistItem
                key={item.key}
                item={item}
                value={answers[item.key]}
                onChange={(v) => handleAnswerChange(item.key, v)}
                remark={remarks[item.key]}
                onRemarkChange={(v) => setRemarks((prev) => ({ ...prev, [item.key]: v }))}
                photos={photos[item.key]}
                onPhotosChange={(files) => setPhotos((prev) => ({ ...prev, [item.key]: files }))}
                lastOdometerKm={lastOdometerKm}
                lastOdometerDate={lastOdometerDate}
                odometerAssessment={odometerAssessment}
              />
            ))}
          </div>
        )}

        <div className="bg-yellow-50 rounded-lg p-3 flex items-start gap-2">
          <AlertCircle className="w-4 h-4 text-yellow-600 flex-shrink-0 mt-0.5" />
          <p className="text-xs text-yellow-700">
            Flagged items notify the vehicle custodian automatically. This does not send the vehicle
            to a workshop — use &quot;Send to Workshop&quot; separately if it needs service.
          </p>
        </div>
      </form>
      </Modal>

      <ConfirmModal
        isOpen={showOdometerConfirm}
        title="Submit odometer for review?"
        confirmLabel="Submit for Review"
        cancelLabel="Check Reading"
        variant="primary"
        theme="employee"
        isLoading={isLoading}
        onCancel={() => setShowOdometerConfirm(false)}
        onConfirm={() => {
          setShowOdometerConfirm(false);
          submitInspection();
        }}
      >
        <div className="space-y-3">
          <div className="rounded-xl border border-amber-200 bg-amber-50 p-3">
            <p className="text-sm text-amber-900 flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
              <span>
                You entered{' '}
                <span className="font-semibold tabular-nums">
                  {formatKm(odometerAssessment?.value)} km
                </span>
                . {odometerAssessment?.detail}
              </span>
            </p>
          </div>
          {odometerAssessment?.suggestedKm != null && (
            <p className="text-sm text-gray-700">
              Check the dashboard carefully. Did you mean{' '}
              <span className="font-semibold tabular-nums">
                {formatKm(odometerAssessment.suggestedKm)} km
              </span>
              ?
            </p>
          )}
          <p className="text-sm text-gray-600">
            Submitting will complete the safety inspection, but the reading
            will stay out of verified vehicle mileage until the fleet
            custodian reviews it.
          </p>
        </div>
      </ConfirmModal>

      <ConfirmModal
        isOpen={showDiscardConfirm}
        title="Discard inspection?"
        confirmLabel="Discard"
        cancelLabel="Keep Editing"
        variant="danger"
        theme="employee"
        onCancel={() => setShowDiscardConfirm(false)}
        onConfirm={discardAndClose}
      >
        <p className="text-sm text-gray-700">
          Your checklist answers and attached photos have not been submitted.
        </p>
      </ConfirmModal>
    </>
  );
};

export default InspectVehicleModal;
